import { useQuery } from '@tanstack/react-query'
import { useApiFetch } from '../../shared/api'

/** The server's verdict. Mirrors mobile's NpsEligibility field for field. */
export interface NpsEligibilityResponse {
  eligible: boolean
  blockingGate: string | null
  daysSinceRegistration: number
  activityCount: number
  daysSinceLatestDismiss: number | null
  daysSinceLatestSubmit: number | null
  newValueMomentsSinceLastSubmit: number
}

export interface NpsEligibilityResult {
  eligible: boolean
  blockingGate: string | null
  isLoading: boolean
  /** For the NpsPromptShown event. Zero while the verdict is unknown. */
  daysSinceRegistration: number
  daysSinceLatestSubmit: number | null
}

/**
 * Asks the server WHETHER this user may be prompted. Never decides WHEN — that
 * is useNpsPromptTiming's job.
 *
 * This is the web half of the eligibility seam: if "who gets asked" ever becomes
 * a server-side sampled draw, this hook's response type changes and no other web
 * file does.
 *
 * Cached for the whole session (`staleTime: Infinity`): the verdict is derived
 * from cooldowns measured in days, so re-asking on every navigation would spend
 * a request per route change to learn the same answer. A failed request yields
 * `eligible: false` — an unreachable gate is a reason not to ask, never a reason
 * to guess yes.
 */
export function useNpsEligibility(): NpsEligibilityResult {
  const call = useApiFetch()
  const { data, isPending, isError } = useQuery({
    queryKey: ['chronicle', 'nps', 'eligibility'],
    queryFn: () => call<NpsEligibilityResponse>('/chronicle/me/nps-eligibility'),
    staleTime: Infinity,
    retry: false,
  })

  if (isError || isPending || data === undefined) {
    return {
      eligible: false,
      blockingGate: null,
      isLoading: isPending,
      daysSinceRegistration: 0,
      daysSinceLatestSubmit: null,
    }
  }
  return {
    eligible: data.eligible,
    blockingGate: data.blockingGate,
    isLoading: false,
    daysSinceRegistration: data.daysSinceRegistration,
    daysSinceLatestSubmit: data.daysSinceLatestSubmit,
  }
}
